import { FC, MouseEventHandler, useCallback, useEffect, useState } from 'react';
import useBusinessPostApi from '~/lib/useBusinessPostApi';
import Loading from '~/components/Loading';
import {
    svgStringToSVGSVGElement,
    svgElementToBase64,
} from './forms/questions/components/SignaturePanel/Signature.utils';

type ViewUploadProps = {
    name?: string;
    file: string;
    className?: string;
    isImage?: boolean;
};

type UploadedFileContent = { content: unknown; isSvg: boolean };

async function fetchContentFromUrl(
    getUrl: () => Promise<{ url: string }>,
): Promise<UploadedFileContent | undefined> {
    const result = await getUrl();
    const response = await fetch(result?.url);
    const contentType = response.headers.get('Content-Type');
    const isSvg = contentType?.includes('svg');
    if (isSvg) {
        const svgString = await response.text();
        const svgElement = svgStringToSVGSVGElement(svgString);

        if (svgElement) {
            return {
                content: svgElementToBase64(svgElement),
                isSvg,
            };
        }
    } else {
        return {
            content: result?.url,
            isSvg: false,
        };
    }
    return;
}
const ViewUpload: FC<ViewUploadProps> = ({
    file,
    name,
    isImage,
    className,
}) => {
    const [href, setHref] = useState('');
    const { save: getUrl, isSaving: isGettingUrl } = useBusinessPostApi(
        `/storage/url/${encodeURIComponent(file)}`,
    );
    const [isSvg, setIsSvg] = useState(false);

    useEffect(() => {
        if (file && typeof file === 'string') {
            fetchContentFromUrl(getUrl).then(
                (urlContent: UploadedFileContent | undefined) => {
                    if (urlContent) {
                        setHref(urlContent.content as string);
                        setIsSvg(urlContent.isSvg);
                    }
                },
            );
        }
        // Explanation: `getUrl` is excluded to prevent unnecessary/excessive API calls (resulting on 429s)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [file]); // We want ESLint to track `file` but not `getUrl`

    const onClickHandler = useCallback(
        (e: React.MouseEvent<HTMLAnchorElement>) => {
            e.preventDefault();
            const blob = new Blob([atob(href.split(',')[1])], {
                type: 'image/svg+xml',
            });
            const blobUrl = URL.createObjectURL(blob);
            window.open(blobUrl, '_blank');
        },
        [href],
    );

    if (!href) {
        return isGettingUrl ? <Loading /> : null;
    }
    const isUrl = href?.includes('http');

    return (
        <div className={className}>
            <a
                href={href}
                onClick={!isUrl ? onClickHandler : undefined}
                target="_blank"
                className="flex items-stretch w-full h-full"
            >
                {(!!isImage || !!isSvg) && (
                    <img
                        src={href}
                        className={`flex rounded object-contain${
                            isSvg ? ' max-h-14 max-w-64' : '' // for svg's (typically signatures), restrict size
                        }`}
                    />
                )}
                {name || ''}
            </a>
        </div>
    );
};

export default ViewUpload;
